import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { Storage } from '@aws-amplify/storage';
import { createAsset } from '@dyl/apiClients/backendApi';

const allowedTypes = ['sounds', 'images'];

/**
 * Provides a function for uploading sound/image assets.
 * - Handles both S3 upload and DB registration.
 * - Includes snackbar messages.
 */
export default function useAssetUpload(existing_asset_list, audio_type = undefined) {
    const { enqueueSnackbar } = useSnackbar();

    return useCallback(
        /**
         * @param {'sounds' | 'images'} type
         * @param {File} file
         * @param {boolean} existenceCheck
         */
        async function uploadAsset(type, file, existenceCheck = true) {
            if ((/\s/).test(file.name.trim())) throw new Error(
                `Invalid asset name. Spaces in the file name are not allowed.`
            );

            if (!allowedTypes.includes(type)) throw new Error(
                `Invalid asset type "${type}". Expected one of the following: ${allowedTypes.join()}`
            );

            const options = {
                customPrefix: { public: '' },
                cacheControl: 'max-age=86400',
                // Should always be specified, ensures things like SVGs don't break
                contentType: file.type,
            };

            if (existenceCheck) {
                let alreadyExists = false;
                switch (type) {
                    case 'images':
                        alreadyExists = existing_asset_list.map(x => x.image_id.toLowerCase()).some(x => x === file.name.toLowerCase());
                        break;
                    case 'sounds':
                        alreadyExists = existing_asset_list.map(x => x.audio_id.toLowerCase()).some(x => x === file.name.toLowerCase());
                        break;
                }

                if (alreadyExists) {
                    const msg = `${type === 'images' ? 'An image' : 'An audio file'} with this name already exists, it can not be overwritten.`;
                    throw new Error(msg);
                }
            }

            try {
                const uri = `${type}/${file.name}`;
                await Storage.put(uri, file, options);
            }
            catch (error) {
                console.error(error);
                enqueueSnackbar('Failed to upload file to S3', { variant: 'error' });
                throw error;
            }

            try {
                await createAsset({
                    name: file.name,
                    type,
                    createDbEntry: true,
                    audio_type
                });
            }
            catch (error) {
                enqueueSnackbar('Failed to register file in database', { variant: 'error' });
                throw error;
            }

            return file.name;
        }, [existing_asset_list]
    );
}